import {
    ForwardedRef,
    forwardRef,
    SyntheticEvent,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import { Coordinates, Range } from 'types'
import { MediaSprite } from '../../../types'
import { ArtboardContext } from '../board/context'
import { ArtboardLoopContext } from '../loop/context'
import { ArtboardLayerCanvasRef } from '../types/refs'

type Props = {
    className?: string
    clipPath?: string
    height: number
    mirror?: boolean
    sprite: MediaSprite
    timing?: Range
    width: number
    onContextMenu?: (e: SyntheticEvent<HTMLElement, Event>) => void
}

const ArtboardLayerCanvas = (
    { className, clipPath, height, mirror = false, sprite, timing, width, onContextMenu }: Props,
    ref: ForwardedRef<ArtboardLayerCanvasRef>
) => {
    const { duration, fps } = useContext(ArtboardContext)
    const { progress: loopProgress } = useContext(ArtboardLoopContext)
    const canvasRef = useRef<HTMLCanvasElement>(null)

    const frameIndexes = useMemo(() => {
        const { numFrames } = sprite
        const indexes: number[] = []

        for (let i = 0; i < numFrames; i++) {
            indexes.push(i)
        }

        return indexes
    }, [sprite])

    const getSpriteFrameCoordinates = (progress: number): Coordinates | undefined => {
        const durationInFrames = Math.round(duration * fps)
        const currentTime = duration * progress
        const startTime = timing?.start || 0
        const endTime = timing?.end || duration

        if (currentTime < startTime || currentTime > endTime) {
            return
        }

        const { columns, frameHeight, frameWidth } = sprite
        const loops = Math.round(durationInFrames / frameIndexes.length)
        const spriteProgress = (progress * loops) % 1
        const spriteFrame = frameIndexes[Math.floor(spriteProgress * frameIndexes.length)]

        return { x: (spriteFrame % columns) * frameWidth, y: Math.floor(spriteFrame / columns) * frameHeight }
    }

    useImperativeHandle(ref, () => ({
        getSpriteFrameCoordinates: (frame: number) => {
            const durationInFrames = Math.round(duration * fps)
            return getSpriteFrameCoordinates(frame / durationInFrames)
        },
    }))

    useEffect(() => {
        if (!canvasRef.current) return

        const { frameHeight, frameScale = 1, frameWidth } = sprite
        const canvas = canvasRef.current
        const context = canvas.getContext('2d')
        const spriteCoordinates = getSpriteFrameCoordinates(loopProgress)

        canvas.width = Math.round(frameWidth * frameScale)
        canvas.height = frameHeight

        if (context && spriteCoordinates) {
            context.save()
            context.translate(canvas.width / 2, 0)
            context.scale(mirror ? -1 : 1, 1)
            context.drawImage(
                sprite.canvas,
                spriteCoordinates.x,
                spriteCoordinates.y,
                frameWidth * frameScale,
                frameHeight * frameScale,
                -canvas.width / 2,
                0,
                canvas.width,
                canvas.height
            )
            context.restore()
        }
    }, [canvasRef, height, loopProgress, mirror, sprite, width])

    return <canvas className={className} ref={canvasRef} style={{ clipPath }} onContextMenu={onContextMenu} />
}

export default forwardRef(ArtboardLayerCanvas)
